import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {DESTINATION_DATA} from 'destination/destination-prop-types';
import uuidV4 from 'uuid/v4';
import IconChevronUp from '@spothero/icons/chevron-up';
import Button from '@spothero/ui/button';
import AnimationUtils from '@spothero/utils/animation';
import DOMUtils from '@spothero/utils/dom';
import withConfig from 'config/withConfig';
import has from 'lodash/has';
import includes from 'lodash/includes';
import SearchMeta from 'search/SearchMeta';
import SegmentUtils from 'utils/segment';
import SHUtils from 'utils/sh-utils';
import GTMUtils from 'utils/gtm';
import ExperimentUtils from 'utils/experiment';
import {connect} from 'react-redux';
import {compose} from 'redux';
import {notify} from 'notification/notification-actions';
import {getSpotsAirport} from 'spots/spots-actions';
import {getSpotsAirportStructuredData} from 'destination/destination-actions';
import SearchAirportControls from './SearchAirportControls';
import SearchAirportHero from './SearchAirportHero';
import SearchAirportResults from './results/SearchAirportResults';
import SearchAirportStructuredDataCard from './SearchAirportStructuredDataCard';
import ParkingRates from './landing-content/ParkingRates';
import WhyBook from './landing-content/WhyBook';
import CustomerSupport from './landing-content/CustomerSupport';
import TrustedByUsers from './landing-content/TrustedByUsers';
import HowToBook from './landing-content/HowToBook';

class SearchAirport extends PureComponent {
    static propTypes = {
        isMobile: PropTypes.bool.isRequired,
        siteUrl: PropTypes.string.isRequired,
        destination: PropTypes.shape(DESTINATION_DATA).isRequired,
        city: PropTypes.object,
        ends: PropTypes.string,
        hasUserInput: PropTypes.bool,
        loadAirportSpots: PropTypes.func.isRequired,
        loadAirportStructuredDataSpots: PropTypes.func.isRequired,
        starts: PropTypes.string,
        airportStructuredData: PropTypes.array,
        airportLowestDailyRate: PropTypes.number,
        sendNotification: PropTypes.func.isRequired,
    };

    constructor(props) {
        super(props);

        this._setDefaultSearchTimes =
            ExperimentUtils.activate('airport_default_search_times') ===
            'airport_default_search_times_1';
        this._isNewDesignEnabled = ExperimentUtils.isFeatureEnabled(
            'airport_destination_page_new_design'
        );
        this._showSEOComponent = ExperimentUtils.getFeatureVariable({
            featureName: 'airport_destination_page_new_design',
            variableName: 'seo_component',
            type: 'Boolean',
        });
    }

    componentDidMount() {
        const {
            starts,
            ends,
            loadAirportStructuredDataSpots,
            destination: {airport: {iata_code: iataCode} = {}},
        } = this.props;

        this._searchPageContainer = DOMUtils.el('body');
        this._bodyClasses = SHUtils.determineInitialLoadBodyClasses({
            isSearchPage: true,
            isAirport: true,
        });

        DOMUtils.addClass(this._searchPageContainer, this._bodyClasses);

        if (starts && ends) {
            this._loadAirportSpots();
        }

        loadAirportStructuredDataSpots(iataCode);

        if (!includes(document.referrer, 'spothero.com')) {
            // only track this if the user is coming from an outside source not somewhere on our site
            SegmentUtils.track({
                event: 'Search Landing',
            });
        }

        SegmentUtils.track({
            event: 'Airport Landing',
        });

        GTMUtils.push({
            event: 'pageview',
            page: 'search-airport',
        });

        SHUtils.staticScrollTo();
    }

    componentDidUpdate(prevProps, prevState) {
        const {starts: prevStarts, ends: prevEnds} = prevProps;
        const {starts, ends} = this.props;

        if (prevStarts !== starts || prevEnds !== ends) {
            this._loadAirportSpots();
        }
    }

    componentWillUnmount() {
        DOMUtils.removeClass(this._searchPageContainer, this._bodyClasses);
    }

    _onSearchSubmit = () => {
        this._scrollToResults();
    };

    _onViewRatesClick = () => {
        this._scrollToSEODescription();
    };

    _onStructuredDataClick = () => {
        const {isMobile, sendNotification} = this.props;
        const topPos = isMobile
            ? DOMUtils.getDocumentOffset('.AirportDateTimePickers').top - 52
            : 0;

        AnimationUtils.scrollTo('html', topPos);

        sendNotification({
            id: uuidV4(),
            type: 'info',
            message: 'Please search to check availability.',
        });
    };

    _loadAirportSpots = () => {
        this.props.loadAirportSpots().catch(error => {
            if (!has(error, 'isCanceled')) {
                SegmentUtils.track({
                    event: 'Error Message Displayed',
                    properties: {
                        'error message': 'Error fetching spots',
                        'error screen': 'search',
                        'error action': 'fetching spots',
                    },
                });
            }
        });
    };

    _scrollToResults = () => {
        const airportContentTop = DOMUtils.getDocumentOffset(
            '.SearchAirport-content'
        ).top;
        const topPos = this.props.isMobile
            ? airportContentTop + 310 - 52 // height of datepickers - height of nav
            : airportContentTop + 64;

        AnimationUtils.scrollTo('html', topPos);
    };

    _scrollToSEODescription = () => {
        const airportContentTop = DOMUtils.getDocumentOffset(
            '.SearchAirport-description'
        ).top;
        const topPos = this.props.isMobile
            ? airportContentTop - 52 // - height of nav
            : airportContentTop;

        AnimationUtils.scrollTo('html', topPos);
    };

    render() {
        const {
            isMobile,
            destination,
            city,
            hasUserInput,
            airportStructuredData,
            airportLowestDailyRate,
            siteUrl,
        } = this.props;
        const {relative_url: destinationRelativeUrl} = destination;
        const {
            slug: citySlug,
            phone_number: phoneNumber,
            phone_number_friendly: phoneNumberFriendly,
            weekday_hours: hours,
        } = city;
        const {title: destinationTitle} = destination;
        const landingContentClasses = classNames(
            'SearchAirport-container',
            'SearchAirport-landing-content',
            {'with-search-results': hasUserInput}
        );

        return (
            <div className="SearchAirport">
                <SearchMeta />
                <SearchAirportHero
                    destination={destination}
                    city={city}
                    isMobile={isMobile}
                    isNewDesignEnabled={this._isNewDesignEnabled}
                />
                <div className="SearchAirport-content">
                    <div className="SearchAirport-container SearchAirport-datepicker-container">
                        <SearchAirportControls
                            className={
                                this._isNewDesignEnabled
                                    ? 'SearchAirportControls-shortened-hero'
                                    : ''
                            }
                            setDefaultSearchTimes={this._setDefaultSearchTimes}
                            onSubmit={this._onSearchSubmit}
                        />
                    </div>
                    {hasUserInput && <SearchAirportResults />}
                </div>
                {this._isNewDesignEnabled && (
                    <div className={landingContentClasses}>
                        {this._showSEOComponent && (
                            <ParkingRates
                                airportTitle={destinationTitle}
                                lowestDailyRate={airportLowestDailyRate}
                                onViewRatesClick={this._onViewRatesClick}
                            />
                        )}
                        <WhyBook />
                        <CustomerSupport
                            phoneNumber={phoneNumber}
                            phoneNumberFriendly={phoneNumberFriendly}
                            hours={hours}
                        />
                        <TrustedByUsers />
                        <HowToBook />
                    </div>
                )}
                {Boolean(destination.description) && (
                    <div
                        className="SearchAirport-container SearchAirport-description"
                        dangerouslySetInnerHTML={{
                            __html: destination.description,
                        }}
                    />
                )}
                <div className="SearchAirport-container SearchAirport-find-parking-now">
                    <Button
                        color="primary"
                        iconPosition="right"
                        onClick={this._onStructuredDataClick}
                    >
                        Find Parking Now
                        <IconChevronUp />
                    </Button>
                </div>
                {airportStructuredData && airportStructuredData.length > 0 && (
                    <div className="SearchAirport-container SearchAirport-structured-data">
                        <h2>Top Rated Parking Near {destination.title}</h2>
                        <p>
                            Input your dates above to see the full list of
                            parking options available through SpotHero.
                        </p>
                        <ul
                            className="SearchAirport-structured-data-spots"
                            itemScope
                            itemType="http://schema.org/ItemList"
                        >
                            <link
                                itemProp="url"
                                href={`${siteUrl}/${citySlug}/${destinationRelativeUrl}`}
                            />
                            <meta
                                itemProp="numberOfItems"
                                content={airportStructuredData.length}
                            />
                            {airportStructuredData.map((spot, index) => {
                                return (
                                    <li
                                        className="SearchAirport-structured-data-spot-wrapper"
                                        key={spot.facility.id}
                                        itemProp="itemListElement"
                                        itemScope
                                        itemType="http://schema.org/ListItem"
                                    >
                                        <meta
                                            itemProp="position"
                                            content={index + 1}
                                        />
                                        <SearchAirportStructuredDataCard
                                            key={spot.facility.id}
                                            spot={spot}
                                            destinationUrl={`${siteUrl}/${citySlug}/${destinationRelativeUrl}`}
                                            onStructuredDataClick={
                                                this._onStructuredDataClick
                                            }
                                        />
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                )}
            </div>
        );
    }
}

const makeMapStateToProps = () => {
    const mapStateToProps = state => {
        const {
            city: {data: city},
            destination: {
                data: destination,
                airportStructuredData,
                airportLowestDailyRate,
            },
            searchRequest: {starts, ends},
        } = state;

        return {
            destination,
            city,
            starts,
            ends,
            airportStructuredData,
            airportLowestDailyRate,
            hasUserInput: Boolean(starts && ends),
        };
    };

    return mapStateToProps;
};

const mapDispatchToProps = {
    loadAirportSpots: getSpotsAirport,
    loadAirportStructuredDataSpots: getSpotsAirportStructuredData,
    sendNotification: notify,
};

const enhance = compose(
    withConfig(['isMobile', 'siteUrl']),
    connect(
        makeMapStateToProps,
        mapDispatchToProps
    )
);

export default enhance(SearchAirport);
